import {
  Building2,
  FileText,
  Globe,
  type LucideIcon,
  MapPin,
  Shuffle,
} from 'lucide-react';

import {
  formatContractTypes,
  locationTypeLabels,
} from '@/modules/careers/careers';
import type { CareersJobApi, LocationType } from '@/modules/careers/types';
import { Badge } from '@/modules/ui/components/badge/Badge';

const locationTypeIcons: Record<LocationType, LucideIcon> = {
  Onsite: Building2,
  Hybrid: Shuffle,
  Remote: Globe,
};

interface CareersJobFactsProps {
  job: CareersJobApi;
  /** The job page lists contract types as a fact; the list row shows them under the title instead. */
  withContract?: boolean;
}

/** A job's short facts as chips: location, work arrangements and, optionally, contract types. */
export function CareersJobFacts({
  job,
  withContract = false,
}: CareersJobFactsProps) {
  const location = job.location.trim();
  const contract = withContract ? formatContractTypes(job) : '';

  return (
    <ul className="flex flex-wrap gap-2">
      {location && <Fact icon={MapPin} label={location} />}
      {job.locationTypes.map((type) => (
        <Fact
          icon={locationTypeIcons[type]}
          key={type}
          label={locationTypeLabels[type]}
        />
      ))}
      {contract && <Fact icon={FileText} label={contract} />}
    </ul>
  );
}

function Fact({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <li>
      <Badge
        className="bg-muted gap-1.5 rounded-lg border-transparent px-2.5 text-[13px]"
        size="lg"
        variant="outline"
      >
        <Icon aria-hidden className="text-muted-foreground" />
        {label}
      </Badge>
    </li>
  );
}
